import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import auditLogService from '../../../services/auditLogService';
import { toast } from '../../../components/ui/Toast';

const AuditLogDashboard = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [filters, setFilters] = useState({
    eventType: '',
    startDate: '',
    endDate: '',
    success: '',
    searchQuery: '',
    page: 1,
    pageSize: 20,
  });

  useEffect(() => {
    loadLogs();
  }, [filters?.page, filters?.eventType, filters?.success]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const { data, error, pagination: paginationData } = await auditLogService?.getLogs(filters);
      if (error) throw error;

      setLogs(data || []);
      setPagination(paginationData);
    } catch (error) {
      console.error('Error loading audit logs:', error);
      toast?.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: 1, // Reset to first page on filter change
    }));
  };

  const handleSearch = () => {
    loadLogs();
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data, error } = await auditLogService?.exportLogs(filters);
      if (error) throw error;

      const filename = `audit-logs-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
      auditLogService?.downloadCSV(data, filename);
      toast?.success('Audit logs exported successfully');
    } catch (error) {
      console.error('Error exporting audit logs:', error);
      toast?.error('Failed to export audit logs');
    } finally {
      setExporting(false);
    }
  };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleClearFilters = () => {
    setFilters({
      eventType: '',
      startDate: '',
      endDate: '',
      success: '',
      searchQuery: '',
      page: 1,
      pageSize: 20,
    });
  };

  const eventTypeOptions = [
    { value: '', label: 'All Events' },
    { value: 'login', label: 'Login' },
    { value: 'login_failed', label: 'Failed Login' },
    { value: 'logout', label: 'Logout' },
    { value: 'register', label: 'Registration' },
    { value: 'password_change', label: 'Password Change' },
    { value: 'device_added', label: 'Device Added' },
    { value: 'device_removed', label: 'Device Removed' },
    { value: 'session_terminated', label: 'Session Terminated' },
    { value: 'permission_change', label: 'Permission Change' },
    { value: 'security_settings_change', label: 'Security Settings' },
  ];

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'true', label: 'Success' },
    { value: 'false', label: 'Failed' },
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="FileText" size={20} className="text-primary" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-foreground">Audit Log Dashboard</h2>
            <p className="text-sm text-muted-foreground mt-1">
              View security events including login attempts, permission changes, and device activity
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExport}
          disabled={exporting || logs?.length === 0}
          iconName={exporting ? 'Loader2' : 'Download'}
          className={exporting ? 'animate-spin' : ''}
        >
          {exporting ? 'Exporting...' : 'Export CSV'}
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-muted/30 rounded-lg p-4 mb-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Event Type Filter */}
          <Select
            label="Event Type"
            value={filters?.eventType}
            onChange={(e) => handleFilterChange('eventType', e?.target?.value)}
            options={eventTypeOptions}
          />

          {/* Status Filter */}
          <Select
            label="Status"
            value={filters?.success}
            onChange={(e) => handleFilterChange('success', e?.target?.value)}
            options={statusOptions}
          />

          {/* Start Date */}
          <Input
            type="date"
            label="Start Date"
            value={filters?.startDate}
            onChange={(e) => handleFilterChange('startDate', e?.target?.value)}
          />

          {/* End Date */}
          <Input
            type="date"
            label="End Date"
            value={filters?.endDate}
            onChange={(e) => handleFilterChange('endDate', e?.target?.value)}
          />
        </div>

        {/* Search Bar */}
        <div className="flex gap-2">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Search by description or event type..."
              value={filters?.searchQuery}
              onChange={(e) => handleFilterChange('searchQuery', e?.target?.value)}
              onKeyPress={(e) => e?.key === 'Enter' && handleSearch()}
            />
          </div>
          <Button onClick={handleSearch} iconName="Search">
            Search
          </Button>
          <Button variant="outline" onClick={handleClearFilters} iconName="X">
            Clear
          </Button>
        </div>
      </div>

      {/* Logs Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 rounded-full bg-muted animate-pulse"></div>
          </div>
        ) : logs?.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Icon name="FileText" size={48} className="mx-auto mb-3 opacity-50" />
            <p>No audit logs found</p>
            <p className="text-sm mt-1">Try adjusting your filters</p>
          </div>
        ) : (
          <>
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Timestamp</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Event</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Description</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Device</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">IP Address</th>
                </tr>
              </thead>
              <tbody>
                {logs?.map((log) => (
                  <tr key={log?.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                    <td className="py-3 px-4 text-sm text-muted-foreground">
                      {new Date(log?.created_at)?.toLocaleString()}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Icon
                          name={auditLogService?.getEventTypeIcon(log?.event_type)}
                          size={16}
                          className={auditLogService?.getEventTypeColor(log?.event_type)}
                        />
                        <span className="text-sm font-medium text-foreground">
                          {auditLogService?.formatEventType(log?.event_type)}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-foreground">{log?.event_description}</td>
                    <td className="py-3 px-4">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                          log?.success
                            ? 'bg-green-100 text-green-700' :'bg-red-100 text-red-700'
                        }`}
                      >
                        <Icon name={log?.success ? 'CheckCircle' : 'XCircle'} size={12} />
                        {log?.success ? 'Success' : 'Failed'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-muted-foreground">
                      {log?.device_info?.browser && log?.device_info?.os
                        ? `${log?.device_info?.browser} on ${log?.device_info?.os}`
                        : 'N/A'}
                    </td>
                    <td className="py-3 px-4 text-sm text-muted-foreground">
                      {log?.ip_address || 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {pagination && pagination?.totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
                <div className="text-sm text-muted-foreground">
                  Showing {(pagination?.page - 1) * pagination?.pageSize + 1} to{' '}
                  {Math.min(pagination?.page * pagination?.pageSize, pagination?.total)} of{' '}
                  {pagination?.total} results
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination?.page - 1)}
                    disabled={pagination?.page === 1}
                    iconName="ChevronLeft"
                  >
                    Previous
                  </Button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, pagination?.totalPages) }, (_, i) => {
                      const pageNum = i + 1;
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                            pagination?.page === pageNum
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted text-muted-foreground hover:bg-muted/80'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination?.page + 1)}
                    disabled={pagination?.page === pagination?.totalPages}
                    iconName="ChevronRight"
                    iconPosition="right"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Info Banner */}
      <div className="mt-6 bg-muted/50 rounded-lg p-4 flex items-start gap-3">
        <Icon name="Info" size={16} className="text-muted-foreground flex-shrink-0 mt-0.5" />
        <div className="text-xs text-muted-foreground">
          <p className="font-medium mb-1">About Audit Logs</p>
          <p>
            Audit logs track all security-related events in your account. Review them regularly to monitor
            for suspicious activity. Logs are retained for 90 days and can be exported for compliance purposes.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuditLogDashboard;